import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Check,
  Loader2,
  X,
  ShieldCheck,
  XCircle,
  MessageSquare,
  Phone,
  Zap,
  HeadphonesIcon,
} from "lucide-react";
import { Organization } from "@/types/supabase";
import { useState } from "react";

interface AvailablePlansTabProps {
  organization: Organization | null;
  processing: boolean;
  onSubscribe: (planId: string) => void;
  hideRedX?: boolean;
  showGuarantee?: boolean;
  showDiscountPricing?: boolean;
  plans?: any[]; // Optional plans prop for database plans
}

// Plan name to UUID mapping
const PLAN_ID_MAPPING = {
  starter: "cbc50e07-e21b-42af-a3dd-9c1c8d9a6334",
  pro: "28d9a7cd-1733-4490-a79c-534eab14b093",
  business: "fb601a9a-f5d2-4ba0-999c-7f3c65c0ce43",
  "business+": "913eef09-6a21-41c3-98a9-dcb621d499cc",
} as const;

const renderFeature = (
  hasFeature: boolean,
  text: string,
  hideRedX: boolean = false,
) => {
  if (hideRedX && !hasFeature) {
    return null;
  }

  return (
    <li className="flex items-start">
      {hasFeature ? (
        <Check className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
      ) : (
        <X className="h-4 w-4 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
      )}
      <span className={`text-sm ${!hasFeature ? "text-gray-500" : ""}`}>
        {text}
      </span>
    </li>
  );
};

const plans = [
  {
    id: "starter",
    name: "Starter",
    price: 69,
    features: [
      "30+ ready-to-use AI assistants powered by the latest models",
      "Create your own assistants",
      "700 AI outputs/month",
      "Live web access",
      "Share assistants publicly",
      "Customize assistants",
    ],
  },
  {
    id: "pro",
    name: "Pro",
    price: 149,
    everythingInPrevious: "Everything in STARTER +",
    additionalFeatures: [
      "2,000 AI outputs/month",
      "AI Phone Agents",
      "450 voice minutes (~4/6 calls/day)",
      "$0.3/min overage",
      "Teams: Invite additional users ($19/u/m)",
      "Max 15 knowledge sources (files, websites, snippets)",
      "Connect to your CRM and 5,000 other tools",
      "Priority support",
    ],
  },
  {
    id: "business",
    name: "Business",
    price: 349,
    everythingInPrevious: "Everything in PRO +",
    additionalFeatures: [
      "4,000 AI outputs/month",
      "More AI Phone agents",
      "1,500 voice minutes",
      "$0.21/min overage",
      "Advanced integrations",
      "Custom voice cloning",
      "Dedicated account manager",
    ],
  },
];

const sharedFeatures = [
  "Chat branding (logo, colors)",
  "Collect information (fields)",
  "Conversation inbox + Email alerts",
  "Zapier / webhook integration",
  "Live access to your website",
];

const AvailablePlansTab = ({
  organization,
  processing,
  onSubscribe,
  hideRedX = false,
  showGuarantee = false,
  showDiscountPricing = false,
  plans: propPlans,
}: AvailablePlansTabProps) => {
  const [processingPlanId, setProcessingPlanId] = useState<string | null>(null);

  const handleSubscribe = async (planId: string) => {
    setProcessingPlanId(planId);
    try {
      // If we have database plans, planId is already a UUID
      if (propPlans && propPlans.length > 0) {
        await onSubscribe(planId);
      } else {
        // Otherwise, map plan name to UUID
        const mappedPlanId =
          PLAN_ID_MAPPING[planId as keyof typeof PLAN_ID_MAPPING];
        if (mappedPlanId) {
          await onSubscribe(mappedPlanId);
        } else {
          console.error("Invalid plan name:", planId);
        }
      }
    } finally {
      setProcessingPlanId(null);
    }
  };

  const isPlanCurrent = (planId: string) => {
    // If we have database plans, planId is already a UUID
    if (propPlans && propPlans.length > 0) {
      return organization?.plan_id === planId;
    }

    // Otherwise, map plan name to UUID
    const mappedPlanId =
      PLAN_ID_MAPPING[planId as keyof typeof PLAN_ID_MAPPING];
    return organization?.plan_id === mappedPlanId;
  };

  // Helper function to get the current plan price
  const getCurrentPlanPrice = () => {
    if (!organization?.plan_id) return 0;

    // Find current plan in the plans array
    const currentPlan = plans.find((plan) => {
      const mappedPlanId =
        PLAN_ID_MAPPING[plan.id as keyof typeof PLAN_ID_MAPPING];
      return organization.plan_id === mappedPlanId;
    });

    return currentPlan?.price || 0;
  };

  // Helper function to determine button text
  const getButtonText = (planId: string, planPrice: number) => {
    if (isPlanCurrent(planId)) {
      return "Current Plan";
    }

    const hasActiveSubscription =
      organization?.subscription_status === "active" ||
      organization?.subscription_status === "trialing";

    if (!hasActiveSubscription) {
      return "Start free trial";
    }

    const currentPrice = getCurrentPlanPrice();
    if (planPrice > currentPrice) {
      return "Upgrade";
    } else if (planPrice < currentPrice) {
      return "Downgrade";
    } else {
      return "Switch plan";
    }
  };

  // Use database plans if available, otherwise use hardcoded plans
  // Filter out Business+ plan
  const filteredPlans = plans.filter((plan) => plan.id !== "business+");
  const plansToRender =
    propPlans && propPlans.length > 0
      ? propPlans.filter(
          (plan) => plan.id !== "913eef09-6a21-41c3-98a9-dcb621d499cc",
        ) // Business+ UUID
      : filteredPlans;

  return (
    <div className="w-full px-4 sm:px-0">
      <div className="grid grid-cols-1 gap-6 sm:gap-8 md:grid-cols-2 xl:grid-cols-3 mb-8 max-w-7xl mx-auto">
        {plansToRender.map((plan) => (
          <Card
            key={plan.id}
            className={`flex flex-col relative bg-white rounded-2xl sm:rounded-3xl shadow-sm border border-gray-100 min-w-0 ${
              isPlanCurrent(plan.id) ? "ring-2 ring-primary border-primary" : ""
            }`}
          >
            {isPlanCurrent(plan.id) && (
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-primary text-primary-foreground px-3 py-1 rounded-full text-sm font-medium">
                Current Plan
              </div>
            )}
            <CardHeader className="p-4 sm:p-6">
              <CardTitle className="text-xl sm:text-2xl font-bold">
                {plan.name}
              </CardTitle>
              <div className="mt-3 sm:mt-4 flex items-baseline">
                <span
                  className="text-3xl sm:text-4xl font-bold tracking-tight"
                  style={{ color: "#8aa3d9" }}
                >
                  ${plan.price}
                </span>
                <span className="ml-1 text-lg sm:text-xl text-gray-500">
                  /month
                </span>
              </div>

              <Button
                className="w-full text-base"
                size="lg"
                onClick={() => handleSubscribe(plan.id)}
                disabled={
                  processingPlanId === plan.id || isPlanCurrent(plan.id)
                }
                style={{ backgroundColor: "#8aa3d9", borderColor: "#8aa3d9" }}
                onMouseEnter={(e) => {
                  if (processingPlanId !== plan.id && !isPlanCurrent(plan.id)) {
                    e.currentTarget.style.backgroundColor = "#7691d1";
                  }
                }}
                onMouseLeave={(e) => {
                  if (processingPlanId !== plan.id && !isPlanCurrent(plan.id)) {
                    e.currentTarget.style.backgroundColor = "#8aa3d9";
                  }
                }}
              >
                {processingPlanId === plan.id ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  getButtonText(plan.id, plan.price)
                )}
              </Button>

              <div className="flex items-center justify-center mt-2">
                <XCircle
                  className="h-4 w-4 mr-1"
                  style={{ color: "#8aa3d9" }}
                />
                <span className="text-sm text-gray-500">Cancel anytime</span>
              </div>

              {showGuarantee && (
                <div className="flex items-center justify-center mt-1">
                  <ShieldCheck
                    className="h-4 w-4 mr-1"
                    style={{ color: "#8aa3d9" }}
                  />
                  <span className="text-sm text-gray-500">
                    Full 14 days money back guarantee
                  </span>
                </div>
              )}
            </CardHeader>

            <CardContent className="flex-grow p-4 sm:p-6 pt-0">
              {/* Main Features */}
              <ul className="space-y-2 mb-4 sm:mb-6">
                {(plan.features || []).map((feature, index) => (
                  <li key={index} className="flex items-start">
                    {typeof feature === "object" ? (
                      <>
                        {feature.hasFeature ? (
                          <Check className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                        ) : (
                          <X className="h-4 w-4 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                        )}
                        <span
                          className={`text-xs sm:text-sm font-medium break-words ${!feature.hasFeature ? "text-gray-500" : ""}`}
                        >
                          {feature.text}
                        </span>
                      </>
                    ) : (
                      <>
                        <Check className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-xs sm:text-sm font-medium break-words">
                          {feature}
                        </span>
                      </>
                    )}
                  </li>
                ))}
              </ul>

              {/* Everything in Previous Plus */}
              {plan.everythingInPrevious && plan.additionalFeatures && (
                <div className="mb-4 sm:mb-6">
                  <h4 className="text-xs sm:text-sm font-semibold text-gray-700 mb-2 sm:mb-3">
                    {plan.everythingInPrevious}
                  </h4>
                  <ul className="space-y-2">
                    {plan.additionalFeatures.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <Check className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-xs sm:text-sm break-words">
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Included in All Subscriptions */}
      <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-gray-200 shadow-sm max-w-7xl mx-auto">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 text-left">
          Included in all subscriptions
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {sharedFeatures.map((feature, index) => (
            <div key={index} className="flex items-start">
              <Check className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
              <span className="text-xs sm:text-sm text-gray-700 break-words">
                {feature}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AvailablePlansTab;
